/** Parse dotted CFR section ids (e.g. `488.30`) into numeric parts for ordering. */
export function parseCfrSectionNumericParts(id: string): number[] {
  return id.split(".").map((part) => {
    const n = Number.parseInt(part, 10);
    return Number.isFinite(n) ? n : Number.NaN;
  });
}

/** Compare two dotted CFR section ids numerically (`488.6` < `488.30`). */
export function compareCfrSectionIds(a: string, b: string): number {
  const aParts = parseCfrSectionNumericParts(a);
  const bParts = parseCfrSectionNumericParts(b);
  const len = Math.max(aParts.length, bParts.length);

  for (let i = 0; i < len; i += 1) {
    const av = aParts[i] ?? Number.NaN;
    const bv = bParts[i] ?? Number.NaN;

    if (Number.isNaN(av) && Number.isNaN(bv)) {
      continue;
    }
    if (Number.isNaN(av)) {
      return 1;
    }
    if (Number.isNaN(bv)) {
      return -1;
    }
    if (av !== bv) {
      return av - bv;
    }
  }

  return 0;
}

type CfrSortableSection = {
  section_identifier?: string | null;
  ecfr_identifier?: string | null;
};

export function cfrSectionSortKey(section: CfrSortableSection): string {
  return section.ecfr_identifier || section.section_identifier || "";
}

/** Return a sorted copy; does not mutate the input array. */
export function sortCfrSectionsByNumericId<T extends CfrSortableSection>(
  sections: T[],
): T[] {
  return [...sections].sort((a, b) =>
    compareCfrSectionIds(cfrSectionSortKey(a), cfrSectionSortKey(b)),
  );
}
